import { getNavbarHeight } from './layout'

export function getInnerWidth(el: Element | string | null | undefined) {
  let _el = el
  if (typeof _el === 'string') {
    _el = document.querySelector(_el)
  }
  if (!_el) {
    return 0
  }
  const style = getComputedStyle(_el)
  return (
    Number.parseFloat(style.width) -
    Number.parseFloat(style.paddingLeft) -
    Number.parseFloat(style.paddingRight)
  )
}

export function onAllTransitionsEnded(
  el: HTMLElement,
  callback: () => void,
  args: { signal?: AbortSignal } = {}
): void {
  // If the element is not animated, we can trigger the callback immediately
  if (el.getAnimations().length < 1) {
    callback()
    return
  }

  el.addEventListener(
    'transitionend',
    function listener() {
      if (el.getAnimations().length > 0) {
        return
      }
      el.removeEventListener('transitionend', listener)
      callback()
    },
    { signal: args.signal }
  )
}

export function determineScrollContainer(reference: HTMLElement | null) {
  // determine which closest parent is currently a scrollable
  let current: HTMLElement | null = reference
  while (current) {
    if (current.scrollHeight > current.clientHeight) {
      return current
    }
    current = current.parentElement
  }
  return null
}

export function scrollIntoView(
  element: HTMLElement,
  options?: {
    behavior?: ScrollBehavior
    includeNavbar?: boolean
    offset?: number
  }
) {
  const {
    behavior = 'smooth',
    includeNavbar = true,
    offset = 0
  } = options ?? {}

  const scrollContainer = determineScrollContainer(element)
  if (!scrollContainer) return

  const elementRect = element.getBoundingClientRect()
  const containerRect =
    scrollContainer === document.documentElement
      ? { top: 0 }
      : scrollContainer.getBoundingClientRect()
  const relativeTop = elementRect.top - containerRect.top
  const scrollTop = scrollContainer.scrollTop

  const navbarHeight = getNavbarHeight()
  const baseOffset = includeNavbar ? -navbarHeight : 0

  scrollContainer.scrollTo({
    top: scrollTop + relativeTop + baseOffset + (offset ?? 0),
    behavior: behavior
  })
}

function tick() {
  return new Promise((resolve) => requestAnimationFrame(resolve))
}

export function onIframeLoad(
  iframe: HTMLElement,
  callback: (iframe: HTMLIFrameElement) => unknown
) {
  if (!(iframe instanceof HTMLIFrameElement)) {
    return
  }

  // if the iframe is already loaded, we can trigger the callback immediately
  if (iframe.contentWindow?.document?.readyState === 'complete') {
    callback(iframe)
  } else {
    // otherwise, we need to wait for the iframe to load
    iframe.addEventListener('load', function fn() {
      callback(iframe)
      iframe.removeEventListener('load', fn)
    })
  }
}

export class DisplayController {
  private storedDisplay: string | null = null
  private abortController: AbortController | null = null
  private mutationObserver: MutationObserver | null = null

  constructor(private readonly element: HTMLElement) {
    this.element = element
    this.mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'class') {
          if (this.element.classList.contains('show')) {
            this.show()
          } else {
            this.hide()
          }
        }
      }
    })

    if (!this.element.classList.contains('show')) {
      this.element.style.display = 'none'
    }

    this.attachObserver()
  }

  attachObserver() {
    this.mutationObserver?.observe(this.element, {
      attributes: true,
      childList: false,
      subtree: false
    })
  }

  detachObserver() {
    this.mutationObserver?.disconnect()
  }

  dispose() {
    this.detachObserver()
  }

  get elementSize() {
    const { width, height } = this.element.getBoundingClientRect()
    return { width, height }
  }

  async show() {
    if (
      this.element.classList.contains('transitioning') &&
      !this.element.classList.contains('show')
    )
      return
    this.detachObserver()
    this.element.classList.toggle('transitioning', true)
    if (this.storedDisplay || this.element.style.display === 'none') {
      this.element.style.display = this.storedDisplay ?? ''
    }
    this.element.classList.toggle('show', false)
    const previousSize = this.elementSize
    this.element.classList.toggle('show', true)
    await tick()
    const nextSize = this.elementSize
    this.element.style.height = `${previousSize.height}px`
    this.element.offsetHeight
    this.element.style.height = `${nextSize.height}px`
    onAllTransitionsEnded(this.element, () => {
      this.element.style.height = ''
      this.element.style.width = ''
      this.element.classList.toggle('transitioning', false)
      this.attachObserver()
    })
  }

  async hide() {
    if (
      this.element.classList.contains('transitioning') &&
      this.element.classList.contains('show')
    )
      return
    this.detachObserver()
    this.element.classList.toggle('transitioning', true)
    this.element.classList.toggle('show', true)
    const previousSize = this.elementSize
    this.element.classList.toggle('show', false)
    await tick()
    const nextSize = this.elementSize
    this.element.classList.toggle('show', true)
    this.element.offsetHeight
    this.element.style.height = `${previousSize.height}px`
    this.element.offsetHeight
    this.element.classList.toggle('show', false)
    this.element.style.height = `${nextSize.height}px`

    onAllTransitionsEnded(this.element, () => {
      this.element.style.display = 'none'
      this.element.style.height = ''
      this.element.style.width = ''
      this.element.classList.toggle('transitioning', false)
      this.attachObserver()
    })
  }
}
